import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';

export default function TermInsurancePage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="text-blue-600">Term Insurance</span>
            </h1>
            <p className="text-xl text-gray-600">
              Maximum life cover at minimal cost - Protect your family's future
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* What is Term Insurance */}
            <section className="bg-blue-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What is Term Insurance?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Term insurance is a pure life insurance policy that provides financial protection to your family 
                in case of your untimely demise. It offers high coverage at affordable premiums, making it the 
                most cost-effective way to secure your family's financial future.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Unlike traditional insurance plans, term insurance doesn't have any maturity benefits or cash value. 
                This is why premiums are significantly lower - you're paying only for the life cover.
              </p>
            </section>

            {/* Why You Need It */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Why You Need Term Insurance</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="border-2 border-gray-200 rounded-xl p-6">
                  <div className="text-3xl mb-3">👨‍👩‍👧‍👦</div>
                  <h3 className="font-bold text-lg mb-2">Family Protection</h3>
                  <p className="text-gray-600">Replace your income to ensure your family can maintain their lifestyle</p>
                </div>
                <div className="border-2 border-gray-200 rounded-xl p-6">
                  <div className="text-3xl mb-3">🏠</div>
                  <h3 className="font-bold text-lg mb-2">Loan Coverage</h3>
                  <p className="text-gray-600">Cover home loans, car loans so your family isn't burdened with debt</p>
                </div>
                <div className="border-2 border-gray-200 rounded-xl p-6">
                  <div className="text-3xl mb-3">🎓</div>
                  <h3 className="font-bold text-lg mb-2">Children's Education</h3>
                  <p className="text-gray-600">Ensure your children's education dreams aren't compromised</p>
                </div>
                <div className="border-2 border-gray-200 rounded-xl p-6">
                  <div className="text-3xl mb-3">💰</div>
                  <h3 className="font-bold text-lg mb-2">Affordable Premiums</h3>
                  <p className="text-gray-600">₹1 Crore cover starting from just ₹500-800/month for young individuals</p>
                </div>
              </div>
            </section>

            {/* Coverage Calculation */}
            <section className="bg-gradient-to-br from-blue-600 to-green-600 text-white rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4">How Much Coverage Do You Need?</h2>
              <p className="mb-4 text-blue-100">
                A general rule of thumb: Your term insurance cover should be <strong>10-15 times your annual income</strong>
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 text-gray-900 rounded-lg p-4 mb-6">
                <p className="text-sm font-semibold text-yellow-800 mb-1">💡 Important Recommendation</p>
                <p className="text-sm text-gray-700">
                  Considering current inflation and rising living costs, we recommend a <strong>minimum coverage of ₹1 Crore (₹1,00,00,000)</strong> regardless of income level to ensure adequate financial protection for your family.
                </p>
              </div>
              <div className="bg-white text-gray-900 rounded-xl p-6">
                <h3 className="font-bold mb-3">Example:</h3>
                <ul className="space-y-2">
                  <li>📊 Annual Income: ₹6,00,000</li>
                  <li>✅ Recommended Cover: ₹1,00,00,000 - ₹1,50,00,000 (₹1Cr - ₹1.5Cr)</li>
                  <li>💵 Approximate Premium (Age 30): ₹12,000 - ₹18,000 per year</li>
                </ul>
                <p className="text-xs text-gray-500 mt-3">
                  *Higher coverage accounts for inflation, future goals (child education, marriage), and outstanding liabilities
                </p>
              </div>
            </section>

            {/* Key Features */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Features & Benefits</h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">100% Tax-Free Death Benefit:</strong>
                    <span className="text-gray-700"> Your nominee receives the entire sum assured completely tax-free under Section 10(10D) - No deductions, no taxes on the claim amount</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Tax Benefits on Premiums:</strong>
                    <span className="text-gray-700"> Premiums paid are tax-deductible under Section 80C (up to ₹1.5L annually)</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Flexible Tenure:</strong>
                    <span className="text-gray-700"> Choose coverage period from 10-40 years based on your needs and life stage</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Multiple Payout Options:</strong>
                    <span className="text-gray-700"> Lump sum, monthly income, or combination based on your family's financial needs</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Comprehensive Riders Available:</strong>
                    <span className="text-gray-700"> Add accidental death benefit, critical illness cover, or disability riders for enhanced protection</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Return of Premium Option:</strong>
                    <span className="text-gray-700"> Choose plans that return all premiums if you survive the policy term (slightly higher premium)</span>
                  </div>
                </li>
              </ul>
            </section>

            {/* Why Choose Us */}
            <section className="bg-green-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Get Term Insurance Through Us?</h2>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Compare quotes from 10+ top insurance companies</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Expert guidance on choosing the right coverage amount</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Help with medical tests and documentation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Lifetime support for claims and policy servicing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">No hidden charges - complete transparency</span>
                </li>
              </ul>
            </section>

            {/* Licensed Advisor Badge */}
            <section className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-2xl p-8">
              <div className="flex items-start gap-6">
                <div className="bg-blue-600 rounded-xl p-4 flex-shrink-0">
                  <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Licensed Term Insurance Advisor</h3>
                  <p className="text-gray-700 mb-4">
                    K Satish is officially licensed with SBI Life Insurance Company Ltd, ensuring you receive 
                    professional and compliant term insurance advice for maximum family protection.
                  </p>
                  <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-600 mb-1 font-semibold">Insurance Company:</p>
                        <p className="text-sm font-bold text-blue-800">SBI Life Insurance Company Ltd</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1 font-semibold">License Code:</p>
                        <p className="text-lg font-mono font-bold text-blue-700">SLI991485554</p>
                      </div>
                    </div>
                    <a
                      href="https://sateeshkotta991485554.lifemitra.com/product/protection-plans.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all font-semibold text-sm"
                    >
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      View Term Insurance Protection Plans
                    </a>
                    <p className="text-xs text-gray-500 text-center mt-2">Click to verify my credentials and explore term insurance options</p>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="text-center bg-blue-600 text-white rounded-2xl p-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Get Your Free Term Insurance Quote Today
              </h2>
              <p className="text-blue-100 mb-6">
                Speak with K Satish for personalized recommendations
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/#contact"
                  className="inline-block bg-white text-blue-600 px-8 py-4 rounded-xl hover:bg-gray-50 transition-all shadow-lg font-semibold"
                >
                  Request Free Consultation
                </Link>
                <a
                  href="https://wa.me/919248852187?text=Hi, I'm interested in Term Insurance"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-green-500 text-white px-8 py-4 rounded-xl hover:bg-green-600 transition-all shadow-lg font-semibold"
                >
                  WhatsApp Now
                </a>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
